import { Input, Textarea } from "@mantine/core"
import { Button, Modal } from "@shopify/polaris"
import React, { useContext, useState } from "react"
import DOMPurify from "dompurify"
import { Spinner } from "./Spinner.jsx"
import { GlobalStateContext } from "./providers/GlobalStateProvider.jsx"
import { useTranslation } from "react-i18next"
import { useAuthenticatedFetch } from "../hooks/useAuthenticatedFetch.js"

export const CreateFeedbackPostModal = ({ show, setShow }) => {
	const { state } = useContext(GlobalStateContext)
	const { t } = useTranslation()

	const [feedbackTitle, setFeedbackTitle] = useState("")
	const [feedbackDetails, setFeedbackDetails] = useState("")

	const [loading, setLoading] = useState(false)
	const [showSuccess, setShowSuccess] = useState(false)
	const hasUser = Boolean(state.userId)
	const isSubmitDisabled =
		!hasUser || feedbackTitle.length === 0 || feedbackDetails.length === 0
	const authenticatedFetch = useAuthenticatedFetch()

	const onCloseModal = () => {
		setFeedbackTitle("")
		setFeedbackDetails("")
		setShow(false)
		setShowSuccess(false)
	}

	const createPost = async () => {
		if (!hasUser) {
			return
		}

		const cleanFeedbackTitle = DOMPurify.sanitize(feedbackTitle)
		const cleanFeedbackDetails = DOMPurify.sanitize(feedbackDetails)

		setLoading(true)
		const response = await authenticatedFetch("/api/feedback/createPost", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				title: cleanFeedbackTitle,
				details: cleanFeedbackDetails,
				email: state.userEmail,
			}),
		}).catch((error) => {
			console.error("Error:", error)
			return null
		})

		const data = await response?.json().catch(() => null)
		if (data?.success === true) {
			setShowSuccess(true)
		}
		setLoading(false)
	}

	return (
		<Modal
			small
			open={show}
			onClose={onCloseModal}
			title={t("components.feedback.add_your_feedback")}
		>
			<div style={{ padding: "1rem" }}>
				{showSuccess ? (
					<p style={{ fontSize: "0.9rem", margin: "0.5rem" }}>
						{t("components.feedback.thank_you_for_your_feedback")}
					</p>
				) : (
					<>
						<h2 style={{ marginBottom: "1rem" }}>
							{t("components.feedback.how_can_we_improve_kopy")}
						</h2>

						<Input.Label required>
							{t("components.feedback.title")}
						</Input.Label>
						<Input
							placeholder={t(
								"components.feedback.title_placeholder",
							)}
							radius="md"
							error={
								feedbackTitle.length === 0
									? t(
											"components.feedback.this_field_is_required",
										)
									: null
							}
							value={feedbackTitle}
							onChange={(event) =>
								setFeedbackTitle(event.currentTarget.value)
							}
							style={{ marginBottom: "1rem" }}
						/>

						<Textarea
							placeholder=""
							label={t("components.feedback.details")}
							radius="md"
							error={
								feedbackDetails.length === 0
									? t(
											"components.feedback.this_field_is_required",
										)
									: null
							}
							autosize
							minRows={2}
							maxRows={4}
							value={feedbackDetails}
							onChange={(event) =>
								setFeedbackDetails(event.currentTarget.value)
							}
						/>

						<div style={{ marginTop: "1rem" }}>
							<Button
								fullWidth
								disabled={isSubmitDisabled || loading}
								onClick={createPost}
							>
								{loading ? (
									<Spinner />
								) : (
									t("components.feedback.submit")
								)}
							</Button>
							{!hasUser ? (
								<p
									style={{
										marginTop: "0.5rem",
										fontSize: "0.75rem",
										color: "#777",
										textAlign: "center",
									}}
								>
									{t("components.feedback.sign_in_to_submit")}
								</p>
							) : null}
						</div>
					</>
				)}
			</div>
		</Modal>
	)
}
